"use client";

import { useLocalePathname } from "@i18n/routing";
import { requestLoadAllBelowFoldSections } from "@marketing/home/lib/landing-below-fold-load-controller";
import {
	isSolutionDemoHashHref,
	triggerSolutionDemoEarlyPreload,
} from "@marketing/home/lib/solution-demo-early-preload";
import { useMarketingHomeScrollPending } from "@marketing/shared/components/marketing-home-scroll-pending-context";
import {
	scrollToMarketingAnchor,
	scrollToMarketingAnchorWhenAvailable,
} from "@marketing/shared/lib/marketing-anchor-scroll";
import { isMarketingLandingHashSectionId } from "@marketing/shared/lib/marketing-landing-hash-sections";
import { isMarketingHomeNavSectionId } from "@marketing/shared/lib/marketing-landing-home-sections";
import type * as React from "react";

export interface MarketingLandingHashNavigationDeps {
	localePathname: string;
	setPendingLandingSectionId: (id: string | null) => void;
}

/**
 * Shared behaviour for in-app `/#section` links on the marketing landing: pending nav
 * highlight + {@link scrollToMarketingAnchor} (sticky header offset + hash replace).
 *
 * Call from `<a href="/#…">` handlers with `preventDefault: true` and the click event.
 * Call from buttons via {@link navigateMarketingLandingHash} (no `preventDefault`).
 */
export function runMarketingLandingHashNavigation(
	hashHref: string,
	deps: MarketingLandingHashNavigationDeps,
	opts?: {
		/** When set (e.g. anchor click / keyboard activation), runs before scrolling when the target exists. */
		event?: Pick<React.SyntheticEvent, "preventDefault">;
		onAfterNavigate?: () => void;
	},
): boolean {
	if (typeof window === "undefined") {
		return false;
	}
	if (!hashHref.startsWith("/#")) {
		return false;
	}
	if (isSolutionDemoHashHref(hashHref)) {
		triggerSolutionDemoEarlyPreload();
	}
	const id = hashHref.slice(2);
	const domId = id === "cta" ? "earlyaccess" : id;
	const target = window.document.getElementById(domId);

	opts?.event?.preventDefault();

	if (deps.localePathname === "/" && isMarketingLandingHashSectionId(id)) {
		const pendingId =
			id === "cta"
				? "earlyaccess"
				: isMarketingHomeNavSectionId(id)
					? id
					: null;
		deps.setPendingLandingSectionId(pendingId);
	}

	if (target) {
		scrollToMarketingAnchor(id, hashHref);
		opts?.onAfterNavigate?.();
		return true;
	}

	requestLoadAllBelowFoldSections();
	scrollToMarketingAnchorWhenAvailable(id, hashHref);
	opts?.onAfterNavigate?.();
	return true;
}

export function navigateMarketingLandingHash(
	hashHref: string,
	deps: MarketingLandingHashNavigationDeps,
	opts?: { onAfterNavigate?: () => void },
): void {
	runMarketingLandingHashNavigation(hashHref, deps, opts);
}

export function useMarketingLandingHashNavigation(options?: {
	onAfterNavigate?: () => void;
}) {
	const localePathname = useLocalePathname();
	const { setPendingLandingSectionId } = useMarketingHomeScrollPending();

	const deps: MarketingLandingHashNavigationDeps = {
		localePathname,
		setPendingLandingSectionId,
	};

	return {
		onHashHrefClick: (event: React.SyntheticEvent, hashHref: string) => {
			runMarketingLandingHashNavigation(hashHref, deps, {
				event,
				onAfterNavigate: options?.onAfterNavigate,
			});
		},
		navigateToHashHref: (hashHref: string) => {
			runMarketingLandingHashNavigation(hashHref, deps, {
				onAfterNavigate: options?.onAfterNavigate,
			});
		},
	};
}
